import { IActionFn } from '@/store/types';
import { clearStorage, getStorage, setStorage } from '@/utils/storage';
import { IAuthRecord, IModuleRecord, IModuleState, IOrgInfo, IUserInfo } from './types';
import { $http } from '@/axios';
import createMenu from '@/menu';
import router from '@/plugins/router';
import store from '@/store';
import { unloadChatbot } from '@/utils/chatbot';

// 登录
export const login: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', true);

    // 登录(登录完成后，获取了 token 才能发送后续请求)
    const authorities: IAuthRecord[] = await $http.post('/auth/login', payload);

    // 查询企业详细信息
    const userInfo: IUserInfo = JSON.parse(getStorage('userInfo') || '{}');
    const orgInfo: IOrgInfo = await $http.post('/v1/data/system/org/detail', { orgId: userInfo.orgId });

    // 普通会员禁止登录(自注册或收费会员降级后的组织为普通会员)
    if (orgInfo?.upgradeToPaidUser === true) {
      clearStorage();
      store.commit('common/setToast', { visible: true, text: '普通会员权限不足，无法登录' });
      return;
    }

    // 查询登录用户所在组织，拥有的增值服务列表
    const modules: IModuleRecord[] = await $http.get('/v1/data/system/org/mymodules');

    const authCodes = authorities.map((item) => item.authority);            // 权限列表
    const moduleCodes = modules.map((item) => item.moduleCode);             // 增值服务列表
    const userMenu = createMenu(orgInfo, userInfo, authCodes, moduleCodes); // 主菜单

    setStorage('orgInfo', orgInfo);         // 存储组织信息
    setStorage('userAuth', authCodes);      // 存储用户的'权限列表'
    setStorage('userModule', moduleCodes);  // 存储组织的'增值服务编码'
    setStorage('userMenu', userMenu);       // 存储主菜单

    // 跳转到首页
    await router.push({ name: 'home' });
  } catch (error) {
    clearStorage();
  } finally {
    commit('setLoading', false);
  }
};

// 当从qt应用传入token后(无密码)，直接查询增值服务列表和权限列表
export const loginWithToken: IActionFn<IModuleState> = async ({ commit }) => {
  try {
    commit('setLoading', true);

    // 查询企业详细信息
    const userInfo: IUserInfo = JSON.parse(getStorage('userInfo') || '{}');
    const orgInfo: IOrgInfo = await $http.post('/v1/data/system/org/detail', { orgId: userInfo.orgId });

    // 普通会员禁止登录(自注册或收费会员降级后的组织为普通会员)
    if (orgInfo?.upgradeToPaidUser === true) {
      clearStorage();
      store.commit('common/setToast', { visible: true, text: '普通会员权限不足，无法登录' });
      return;
    }

    // 查询登录用户拥有的权限列表
    const fetchAuthorities: Promise<IAuthRecord[]> = $http.get('/auth/authorities');

    // 查询登录用户所在组织，拥有的增值服务列表
    const fetchModules: Promise<IModuleRecord[]> = $http.get('/v1/data/system/org/mymodules');

    // 等待所有异步操作完成
    const [authorities, modules] = await Promise.all([fetchAuthorities, fetchModules]);

    const authCodes = authorities.map((item) => item.authority);            // 权限列表
    const moduleCodes = modules.map((item) => item.moduleCode);             // 增值服务列表
    const userMenu = createMenu(orgInfo, userInfo, authCodes, moduleCodes); // 主菜单

    setStorage('userAuth', authCodes);      // 存储用户的'权限列表'
    setStorage('userModule', moduleCodes);  // 存储组织的'增值服务编码'
    setStorage('userMenu', userMenu);       // 存储主菜单

    // 跳转到首页
    await router.push({ name: 'home' });
  } catch (e) {
    clearStorage();
  } finally {
    commit('setLoading', false);
  }
};

// 发送短信验证码
export const sendSms: IActionFn<IModuleState> = ({ commit }, payload) => {
  commit('setSmsState', { loading: true });
  $http.post('/v1/system/sms/sendverificationcode', payload).then((res) => {
    commit('setSmsState', { loading: true, success: res });
  }).catch(() => {
    commit('setSmsState', { loading: false, success: false });
  });
};

// 退出登录
export const logout: IActionFn<IModuleState> = async () => {
  unloadChatbot();
  clearStorage();
  await router.push({ name: 'login' });
};
